/**
 * ResourcesPanel — upload a new resource + manage published resources.
 * Extracted verbatim from Admin.jsx (ResourceUploadForm + published list).
 */
import React, { useState } from "react";
import { toast } from "sonner";
import api from "../../lib/api";
import { formatDate } from "./adminHelpers";

const ResourceUploadForm = ({ onUploaded }) => {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState("Insight");
  const [gated, setGated] = useState(true);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!file) {
      toast.error("Please choose a file");
      return;
    }
    setBusy(true);
    const fd = new FormData();
    fd.append("title", title);
    fd.append("description", description);
    fd.append("category", category);
    fd.append("gated", gated ? "true" : "false");
    fd.append("file", file);
    try {
      await api.post("/admin/resources", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Resource uploaded");
      setTitle(""); setDescription(""); setCategory("Insight"); setGated(true); setFile(null);
      e.target.reset();
      onUploaded?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="ek-surface border ek-line p-6 lg:p-8" data-testid="resource-upload-form">
      <h3 className="ek-heading text-xl mb-5">Upload a new resource</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <label className="block">
          <span className="text-[11px] font-medium tracking-[0.16em] ek-text-muted uppercase">Title</span>
          <input type="text" value={title} onChange={(e) => setTitle(e.target.value)} required maxLength={200} className="mt-2 w-full border ek-line px-3 py-2 text-[14px]" data-testid="resource-title-input" />
        </label>
        <label className="block">
          <span className="text-[11px] font-medium tracking-[0.16em] ek-text-muted uppercase">Category</span>
          <input type="text" value={category} onChange={(e) => setCategory(e.target.value)} required maxLength={80} className="mt-2 w-full border ek-line px-3 py-2 text-[14px]" data-testid="resource-category-input" />
        </label>
      </div>
      <label className="block mt-4">
        <span className="text-[11px] font-medium tracking-[0.16em] ek-text-muted uppercase">Description</span>
        <textarea value={description} onChange={(e) => setDescription(e.target.value)} required rows={3} maxLength={1000} className="mt-2 w-full border ek-line px-3 py-2 text-[14px]" data-testid="resource-description-input" />
      </label>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
        <label className="block">
          <span className="text-[11px] font-medium tracking-[0.16em] ek-text-muted uppercase">File</span>
          <input type="file" onChange={(e) => setFile(e.target.files?.[0] || null)} required className="mt-2 w-full text-[14px]" data-testid="resource-file-input" />
        </label>
        <label className="flex items-center gap-2 mt-7">
          <input type="checkbox" checked={gated} onChange={(e) => setGated(e.target.checked)} data-testid="resource-gated-checkbox" />
          <span className="text-[14px]">Require visitor details (form-gated download)</span>
        </label>
      </div>
      <button type="submit" disabled={busy} className="ek-btn ek-btn-primary mt-6 disabled:opacity-60" data-testid="resource-upload-submit">
        {busy ? "Uploading…" : "Upload resource"}
      </button>
    </form>
  );
};

const ResourcesPanel = ({ resources, onReload }) => {
  const deleteResource = async (id) => {
    if (!window.confirm("Delete this resource?")) return;
    try {
      await api.delete(`/admin/resources/${id}`);
      toast.success("Resource deleted");
      onReload?.();
    } catch (e) {
      toast.error("Delete failed");
    }
  };

  return (
    <div data-testid="admin-resources-panel" className="space-y-10">
      <ResourceUploadForm onUploaded={onReload} />
      <div>
        <h3 className="ek-heading text-xl mb-5">Published resources</h3>
        <div className="overflow-x-auto border ek-line">
          <table className="min-w-full text-[13px]">
            <thead className="ek-surface-2 ek-text-muted text-left">
              <tr>
                <th className="px-3 py-2">Date</th><th className="px-3 py-2">Title</th><th className="px-3 py-2">Category</th>
                <th className="px-3 py-2">File</th><th className="px-3 py-2">Gated</th><th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {resources.length === 0 ? (
                <tr><td colSpan={6} className="px-3 py-10 text-center ek-text-muted text-[13px]"><div className="font-medium ek-text-soft">No resources published yet.</div><div className="mt-1 ek-text-muted">Use the form above to publish a new public resource — title, slug, summary and a PDF / external link.</div></td></tr>
              ) : resources.map((r) => (
                <tr key={r.id} className="border-t ek-line" data-testid={`resource-row-${r.id}`}>
                  <td className="px-3 py-2 whitespace-nowrap">{formatDate(r.created_at)}</td>
                  <td className="px-3 py-2 font-medium">{r.title}</td>
                  <td className="px-3 py-2">{r.category}</td>
                  <td className="px-3 py-2">{r.filename} <span className="ek-text-muted">({Math.round(r.size_bytes/1024)} KB)</span></td>
                  <td className="px-3 py-2">{r.gated ? "Yes" : "No"}</td>
                  <td className="px-3 py-2 text-right">
                    <button type="button" onClick={() => deleteResource(r.id)} className="text-[12px] text-red-600 hover:text-red-800 underline" data-testid={`delete-resource-${r.id}`}>
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default ResourcesPanel;
